<script setup lang="ts">
import { CreateFolder } from '~/apis/passageStorage'
import {useCache} from "~/common/useCache";

// GA Analytics 추가
const { trackEvent } = useAnalytics()

const app = useAppStore()
const passageStorage = usePassageStorage()

const folderName = ref('제목없는 폴더')
const isExceedLength = ref(false)
const input = ref<HTMLInputElement | null>(null)

// ESC 키 이벤트 핸들러
const handleEscapeKey = (event: KeyboardEvent) => {
  if (event.key === 'Escape') {
    passageStorage.closeCreateFolder()
  }
}

const handleInput = (event: Event) => {
  const target = event.target as HTMLInputElement
  const previousLength = folderName.value.length
  folderName.value = target.value
  
  // GA 이벤트: 폴더명 입력 상호작용 (100자 단위로 추적)
  const currentLength = target.value.length
  if (currentLength > 0 && Math.floor(currentLength / 5) !== Math.floor(previousLength / 5)) {
    try {
      trackEvent('passagestorage_folder_name_input', {
        event_category: 'form_interaction',
        name_length: currentLength,
        is_default_name: target.value.includes('제목없는 폴더'),
        um_idx: app.user.UM_IDX ?? '',
        timestamp: new Date().toISOString()
      })
    } catch (error) {
      console.warn('GA tracking failed:', error)
    }
  }
  
  // 30자 초과 체크
  // isExceedLength.value = target.value.length > 30
}

// 폴더 추가
const createFolder = async () => {
  if (folderName.value.length === 0) return

  const createStartTime = Date.now()

  try {
    const res = await CreateFolder({
      UM_ID: app.user.UM_ID,
      FOLDER_NAME: folderName.value,
    })

    if (res.data) {
      // GA 이벤트: 폴더 생성 성공
      try {
        trackEvent('passagestorage_folder_creation_success', {
          event_category: 'item_interaction',
          folder_name: folderName.value,
          name_length: folderName.value.length,
          creation_time_ms: Date.now() - createStartTime,
          is_default_name: folderName.value.includes('제목없는 폴더'),
          um_idx: app.user.UM_IDX ?? '',
          timestamp: new Date().toISOString()
        })
      } catch (error) {
        console.warn('GA tracking failed:', error)
      }
      
      passageStorage.closeCreateFolder()
      passageStorage.reload()
    } else {
      console.log('폴더 생성 실패')
    }
  } catch (err) {
    console.log('[ERROR] 폴더 생성 오류 - ', err)
  }
}

// 폴더 생성 취소 핸들러
const handleCancelCreate = () => {
  // GA 이벤트: 폴더 생성 취소
  try {
    trackEvent('passagestorage_folder_creation_cancelled', {
      event_category: 'ui_interaction',
      folder_name: folderName.value,
      name_length: folderName.value.length,
      had_input: folderName.value !== '제목없는 폴더',
      um_idx: app.user.UM_IDX ?? '',
      timestamp: new Date().toISOString()
    })
  } catch (error) {
    console.warn('GA tracking failed:', error)
  }
  
  passageStorage.closeCreateFolder()
}

onMounted(() => {
  // GA 이벤트: 폴더 생성 시작 (팝업 열림)
  try {
    trackEvent('passagestorage_folder_creation_started', {
      event_category: 'ui_interaction',
      action_source: 'new_folder_button',
      default_name: folderName.value,
      um_idx: app.user.UM_IDX ?? '',
      timestamp: new Date().toISOString()
    })
  } catch (error) {
    console.warn('GA tracking failed:', error)
  }

  // ESC 키 이벤트 리스너 등록
  document.addEventListener('keydown', handleEscapeKey)

  // 팝업 오픈시 텍스트가 드래그된 상태로 표시
  nextTick(() => {
    if (input.value) {
      input.value.focus()
      input.value.select()
    }
  })
})
onUnmounted(() => {
  // 이벤트 리스너 해제
  document.removeEventListener('keydown', handleEscapeKey)
})
</script>

<template>
  <!-- 폴더 추가 -->
  <div class="layer active">
    <div class="mask"></div>
    <div class="inner">
      <section class="alert folderAdd">
        <header class="">
          <h2>새 폴더</h2>
          <a class="layerClose" @click="handleCancelCreate"
            >닫기</a
          >
        </header>
        <main>
          <p>
            <input
              ref="input"
              v-model="folderName"
              type="text"
              placeholder="새 폴더"
              :class="`${isExceedLength ? 'error' : ''}`"
              @input="handleInput"
              maxlength="30"
            />
          </p>
          <p v-if="isExceedLength" class="error">30자 이내로 작성해주세요</p>
        </main>
        <footer>
          <div class="btnArea">
            <a @click="handleCancelCreate">취소</a>
            <a class="active" @click="createFolder">만들기</a>
          </div>
        </footer>
      </section>
    </div>
  </div>
  <!-- 폴더 추가 -->
</template>

<style scoped></style>
